import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import React, { useEffect, useState } from "react"
import { validEmailRegex } from "../../../constant/utils"
import { actions, updateUser } from "../../../data/authenticationSlice"
import { PatternFormat } from "react-number-format"
import Loader from "../../../components/Loader"

const { updateSetting, assignUpdatedSettings } = actions

const EditSettings = ({ show, onHide }) => {
    const dispatch = useDispatch()

    const {
        beingUpdatedSettings: settings,
        user: userSettings,
        fetchStatus
    } = useSelector((state) => state.authentication)
    const [errors, setErrors] = useState({
        firstName: null,
        phoneNumber: null,
        email: null,
        passwordValidations: [],
        errorMessage: null
    })

    useEffect(() => {
        setErrors({
            firstName: null,
            phoneNumber: null,
            email: null,
            passwordValidations: [],
            errorMessage: null
        })
        dispatch(assignUpdatedSettings(userSettings))
    }, [show])

    const update = (e) => {
        const { name, value } = e.currentTarget
        if (name === "phoneNumber") {
            dispatch(updateSetting({ field: name, value: value?.replace(/\D/g, "") }))
        } else {
            dispatch(updateSetting({ field: name, value: value }))
        }
    }

    const validateProfileData = () =>
        !settings?.firstName ||
        !settings?.email ||
        !validEmailRegex.test(settings?.email) ||
        settings?.phoneNumber?.length < 10

    const validatePasswords = () => {
        if (settings?.password || settings?.confirmPassword) {
            let passErrors = {}
            if (settings?.password.length < 6) {
                passErrors.password = "Password must be at least 6 characters"
            }
            if (!/\W/.test(settings?.password)) {
                passErrors.nonAlphanumeric = "Password must have at least one non-alphanumeric character"
            }
            if (!/[a-z]/.test(settings?.password)) {
                passErrors.lowercase = "Password must have at least one lowercase letter"
            }
            if (!/[A-Z]/.test(settings?.password)) {
                passErrors.uppercase = "Password must have at least one uppercase letter"
            }
            if (
                !settings?.password ||
                !settings?.confirmPassword ||
                settings?.password !== settings?.confirmPassword ||
                Object.keys(passErrors).length > 0
            ) {
                setErrors({
                    passwordValidations: [
                        !settings?.password ? "Please enter password" : "",
                        !settings?.confirmPassword
                            ? "Please enter confirm password"
                            : settings?.confirmPassword !== settings?.password
                            ? "The confirm password don't match"
                            : "",
                        ...Object.values(passErrors)
                    ]
                })
                return true
            } else return false
        } else return false
    }

    const submitSettings = () => {
        if (JSON.stringify(settings) === JSON.stringify(userSettings)) {
            setErrors({
                ...errors,
                errorMessage: "No new changes were detected."
            })
            return false
        }

        if (validateProfileData()) {
            setErrors({
                firstName: !settings?.firstName ? "Please enter first name" : "",
                email: !settings?.email || !validEmailRegex.test(settings?.email) ? "Please enter a valid email" : "",
                phoneNumber: settings?.phoneNumber?.length < 10 ? "Please enter complete phone number" : ""
            })
            return false
        }

        if (validatePasswords()) {
            return false
        }

        dispatch(updateUser({ ...settings })).then(({ payload }) => {
            if (payload?.succeeded) {
                onHide()
            } else if (!payload?.succeeded && payload?.errorMessage) {
                setErrors({
                    errorMessage: payload?.errorMessage
                })
            }
        })
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit Profile</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader show={fetchStatus === "loading"} />
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="formFirstName">
                                <Form.Label>First Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-control-sm"
                                    name="firstName"
                                    value={settings?.firstName || ""}
                                    onChange={update}
                                />
                                {errors?.firstName && (
                                    <Form.Text className="text-danger">{errors?.firstName}</Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group
                                controlId="formLastName"
                                className="">
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-control-sm"
                                    name="lastName"
                                    value={settings?.lastName || ""}
                                    onChange={update}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group
                        controlId="formEmail"
                        className="mt-2">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            className="form-control-sm"
                            value={settings?.email || ""}
                            onChange={update}
                        />
                        {errors?.email && <Form.Text className="text-danger">{errors?.email}</Form.Text>}
                    </Form.Group>

                    <Form.Group
                        controlId="phoneNumber"
                        className="mt-2">
                        <Form.Label>Phone Number</Form.Label>
                        <PatternFormat
                            format="(###) ###-####"
                            allowEmptyFormatting
                            mask="_"
                            className="form-control form-control-sm pattern-format"
                            name="phoneNumber"
                            value={settings?.phoneNumber || ""}
                            onChange={update}
                        />
                        {errors?.phoneNumber && <Form.Text className="text-danger">{errors?.phoneNumber}</Form.Text>}
                    </Form.Group>

                    <Row className="mt-2">
                        <Col md={6}>
                            <Form.Group controlId="password">
                                <Form.Label>Password</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="password"
                                    className="form-control-sm"
                                    value={settings?.password || ""}
                                    onChange={update}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group
                                controlId="confirmPassword"
                                className="">
                                <Form.Label>Confirm Password</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="confirmPassword"
                                    className="form-control-sm"
                                    value={settings?.confirmPassword || ""}
                                    onChange={update}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
                {errors?.passwordValidations?.length > 0 && (
                    <div className="mt-2">
                        <ul>
                            {errors?.passwordValidations?.map((error, index) => (
                                <React.Fragment key={index}>
                                    {!!error && (
                                        <li>
                                            <Form.Text className="text-danger">{error}</Form.Text>
                                        </li>
                                    )}
                                </React.Fragment>
                            ))}
                        </ul>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-block">
                <div className="d-flex justify-content-end">
                    <Button
                        className="btn-sm"
                        variant="secondary"
                        onClick={onHide}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        className="ms-2 btn-sm"
                        onClick={submitSettings}>
                        Save
                    </Button>
                </div>
                {errors?.errorMessage && (
                    <div className="d-flex justify-content-end">
                        <Form.Text className="text-danger text-right">{errors?.errorMessage}</Form.Text>
                    </div>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default EditSettings
